/**
 * Structural "this did not come from our code" checks, shared by the Sentry
 * filter (src/instrument.ts) and the client_errors filter
 * (src/lib/errorLogger.ts) so the two cannot drift — the same arrangement
 * @/lib/chunkLoadError uses, per design-rules.md §12.6.
 *
 * WHY STRUCTURAL (2026-08-19)
 * ---------------------------
 * Every browser-extension error so far has been retired by hand-adding its
 * literal message text to two lists. That only ever removes the one string
 * somebody noticed. The 2026-08-19 client_errors export still carried
 * `Cannot redefine property: FunCaptcha`, whose stack names an extension
 * content script outright, because nothing looked at WHERE the frame came
 * from. Origin is the durable signal; message text is a symptom.
 */

/** chrome-, moz-, safari-web-, safari-, ms-browser- extension URL schemes. */
const EXTENSION_SCHEME_RE = /^(chrome|moz|safari-web|safari|ms-browser)-extension:\/\//;

/**
 * `?__cpo=<base64 origin>` is the query signature of a third-party rewriting
 * web proxy that re-serves the whole app from its own host (observed
 * 2026-08-09 on 95.214.53.156, where __cpo decoded to https://spotivibly.com).
 * Rewritten module URLs make React load twice, so context lookups fail in ways
 * that are impossible in our real tree — the export carried
 * "useSpotifyPlayerContext must be used within SpotifyPlayerProvider" even
 * though that provider wraps the whole app in App.tsx. Our own assets are
 * never served with this param.
 */
const REWRITING_PROXY_MARKER = "__cpo=";

/**
 * Injected-script collision messages. A second copy of a script evaluating in
 * the same realm redeclares its own top-level bindings. This cannot come from
 * our bundle: a duplicate top-level declaration in our source fails at build,
 * and the stale-chunk case is matched separately by @/lib/chunkLoadError.
 */
export const INJECTED_SCRIPT_MESSAGE_PATTERNS = [
  "has already been declared",
];

/**
 * Returns the URL of the FIRST stack frame, which is the frame that actually
 * threw. Frame-shaped lines only, so a URL sitting in the message header (a
 * chunk-load error quotes one) cannot be mistaken for an origin.
 *
 * Handles both stack dialects: V8 `    at fn (url:1:2)` and JavaScriptCore /
 * SpiderMonkey `fn@url:1:2`.
 */
export function firstStackFrameUrl(stack?: string | null): string | null {
  if (!stack) return null;
  for (const raw of stack.split("\n")) {
    const line = raw.trim();
    if (!/^at\s/.test(line) && !line.includes("@")) continue;
    const match = /\b[a-z][a-z0-9.+-]*:\/\/[^\s)'"]+/i.exec(line);
    if (match) return match[0];
  }
  return null;
}

/**
 * True when the throwing frame belongs to a browser extension or a rewriting
 * proxy mirror rather than to our own bundle.
 *
 * Deliberately keyed on the FIRST frame, not on any frame. An extension that
 * wraps `window.fetch` appears in the middle of stacks for real bugs in our
 * code; dropping anything that merely mentions an extension would hide them.
 */
export function isThirdPartyOriginStack(stack?: string | null): boolean {
  const url = firstStackFrameUrl(stack);
  if (!url) return false;
  const lower = url.toLowerCase();
  return EXTENSION_SCHEME_RE.test(lower) || lower.includes(REWRITING_PROXY_MARKER);
}
